import { Component, Suspense, lazy, useEffect, useRef } from "react";
import type { ReactNode } from "react";
import { Form } from "react-router";

import { useURL } from "~/contexts";
import SvgSearchIcon from "~/hec/assets/icons/search-icon";
import { SEARCH_QUERY_MAX_LENGTH } from "~/lib/utils/url";
import { reportClientError } from "~/utils/report-client-error";

import {
  adoptPendingSearch,
  stashPendingSearchValue,
} from "./pending-search-input";

// COM-189 wave 4: the widget graph (downshift + CIO client + combobox UI,
// ~83KB pre-minify) is the layout chunk's only path to downshift, and every
// call site is already client-gated (ClientOnly in the header, open-gated
// dialogs in the mobile overlay and meganav) — the widget never SSRs. Loading
// it lazily moves those bytes off the document's modulepreload critical path;
// until the chunk lands the Suspense fallback below renders the same plain
// GET form the error boundary degrades to, so search stays usable throughout.
const CioAutocomplete = lazy(() =>
  import("./cio-autocomplete").then(m => ({ default: m.CioAutocomplete })),
);

type Variant = "meganav" | "header" | "mobile";

type SafeCioAutocompleteProps = {
  variant?: Variant;
  focusOnMount?: boolean;
  onClose?: () => void;
};

/**
 * Degraded-mode search: a plain GET form → /search?q=. Rendered when the
 * autocomplete widget throws (see the boundary below). No CIO client, no
 * combobox — just enough to keep search usable for that session.
 */
function PlainSearchFallback({
  action,
  variant,
}: {
  action: string;
  variant: Variant;
}) {
  const isMobile = variant === "mobile";
  const inputRef = useRef<HTMLInputElement>(null);

  // Keystroke handoff (PR #1042 blocker 4). On mount: adopt anything the
  // shopper already typed into the header's hidden iOS-keyboard warm-up input
  // while the widget chunk streams, and take the focus (keeping the soft
  // keyboard up) so further keystrokes land HERE — visibly — instead of in
  // the invisible warm-up input. On unmount (the Suspense fallback→widget
  // swap): stash the buffered value for the widget to consume — the swap
  // removes this input before the widget's effects can see it.
  useEffect(() => {
    const input = inputRef.current;
    if (!input) return;
    const { value, hadFocus } = adoptPendingSearch();
    if (value) input.value = value;
    if (hadFocus) input.focus({ preventScroll: true });
    return () => {
      if (input.value) stashPendingSearchValue(input.value);
    };
  }, []);

  return (
    <Form
      method="get"
      action={action}
      className="relative flex w-full items-center"
      data-testid="cio-autocomplete-fallback"
    >
      <label htmlFor={`cio-fallback-q-${variant}`} className="sr-only">
        Search products
      </label>
      <input
        ref={inputRef}
        id={`cio-fallback-q-${variant}`}
        type="search"
        name="q"
        maxLength={SEARCH_QUERY_MAX_LENGTH}
        placeholder={
          variant === "meganav"
            ? "Search for a product..."
            : "Search Eligible Products"
        }
        aria-label="Search products"
        autoComplete="off"
        className={
          isMobile
            ? "h-11 w-full rounded-lg border border-foreground bg-white py-2 pl-4 pr-10 font-grotesque text-base text-foreground outline-none focus:border-accent focus:ring-1 focus:ring-accent"
            : "h-14 w-full rounded-lg border border-foreground bg-white py-2 pl-4 pr-16 font-grotesque text-sm text-foreground outline-none transition-colors focus:border-accent focus:ring-1 focus:ring-accent"
        }
      />
      <button
        type="submit"
        className="absolute right-3 top-1/2 -translate-y-1/2 text-accent"
        aria-label="Submit search"
      >
        <SvgSearchIcon
          width={18}
          height={18}
          className="[stroke-width:0.5] lg:[stroke-width:1]"
        />
      </button>
    </Form>
  );
}

class AutocompleteErrorBoundary extends Component<
  { variant: Variant; fallback: ReactNode; children: ReactNode },
  { hasError: boolean }
> {
  state = { hasError: false };

  static getDerivedStateFromError(): { hasError: boolean } {
    return { hasError: true };
  }

  componentDidCatch(error: unknown) {
    // The whole point of SFCC-11911: a widget throw must NOT reach the route
    // error boundary and blank the storefront. Contain it here and report for
    // observability — the original outage returned HTTP 200, invisible in logs.
    reportClientError("cio-autocomplete", error, {
      variant: this.props.variant,
    });
  }

  render() {
    return this.state.hasError ? this.props.fallback : this.props.children;
  }
}

/**
 * SFCC-11911: defense-in-depth wrapper around the CIO autocomplete widget. If
 * the widget throws at runtime (as the React-18 UI lib did under React 19), the
 * boundary degrades to a plain /search GET form for that session instead of
 * letting the throw bubble to the route error boundary and blank the page.
 */
export function SafeCioAutocomplete({
  variant = "meganav",
  ...props
}: SafeCioAutocompleteProps) {
  const url = useURL();
  const fallback = (
    <PlainSearchFallback action={url("/search")} variant={variant} />
  );
  return (
    <AutocompleteErrorBoundary variant={variant} fallback={fallback}>
      <Suspense fallback={fallback}>
        <CioAutocomplete variant={variant} {...props} />
      </Suspense>
    </AutocompleteErrorBoundary>
  );
}
